import { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '@/contexts/AuthContext';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useUserStore } from '@core/user/useUserStore';
import { useThemeStore } from '@core/theme/useThemeStore';
import { useTenant, useTenantLogoUrl } from '@/contexts/TenantContext';
import { usePortalAccess } from '@/hooks';
import { getSidebarMenuItems } from '@/config/portalRoutes.config';

const Sidebar = () => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { signOut } = useAuth();
  const { currentUser } = useUserStore();
  const { theme } = useThemeStore();
  const logoUrl = useTenantLogoUrl();
  const userName = `${currentUser?.firstName} ${currentUser?.lastName}`;
  const userEmail = currentUser?.email || '';
  const access = usePortalAccess();
  const isTenant = access.isTenantPortal;
  const isHqAdmin = access.isHqAdmin;
  const { tenantInfo } = useTenant();

  const menuItems = useMemo(() => getSidebarMenuItems(access), [access]);

  const userRole = useMemo(() => {
    if (isHqAdmin) {
      return 'HQ Admin';
    } else if (isTenant) {
      return 'Tenant';
    } else {
      return 'Client';
    }
  }, [isHqAdmin, isTenant]);

  // Close menu on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  return (
    <div className="flex h-full w-64 flex-col border-r bg-white p-4 text-black">
      {/* Tenant Logo */}
      <div className="flex h-[100px] w-full items-center justify-center rounded-lg bg-gray-100">
        {logoUrl || theme?.logoUrl ? (
          <img
            src={logoUrl || theme?.logoUrl}
            alt="Tenant Logo"
            className="h-[100px] w-auto object-contain"
          />
        ) : (
          <div className="flex h-[100px] w-full items-center justify-center rounded-lg bg-gray-100">
            <span className="text-md text-gray-500">
              {tenantInfo?.tenantName || 'No logo available'}
            </span>
          </div>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1">
        {menuItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.id}
              to={item.path}
              className={`text-md mt-2 flex items-center rounded-sm px-2 py-3 ${
                isActive
                  ? 'bg-[#03406A] text-white'
                  : 'text-black hover:bg-[#03406A]/10 hover:text-[#03406A]'
              } transition-all duration-200 ease-in-out`}
            >
              <FontAwesomeIcon
                icon={item.icon}
                className={`mr-2 ${isActive ? 'text-white' : 'text-black'} h-6 w-6`}
              />
              <span className={` ${isActive ? 'text-white' : 'text-black'}`}>
                {item.title}
              </span>
            </Link>
          );
        })}
      </nav>

      {/* Help centre Button */}
      {/* <div className="">
        <button
          className="flex w-full items-center justify-start rounded-md p-3 text-sm transition-colors hover:bg-[#03406A]/10 hover:text-[#03406A]"
        >
          <FontAwesomeIcon
            icon={faQuestionCircle}
            className="mr-3 h-4 w-4 text-lg"
          />
          <span className="font-medium">Help centre</span>
        </button>
      </div> */}

      {/* User Menu */}
      <div
        className="relative mb-6 flex justify-between gap-2"
        ref={userMenuRef}
      >
        <div
          className="flex cursor-pointer items-center gap-2 rounded-md p-1 hover:bg-gray-100"
          onClick={() => setUserMenuOpen((open) => !open)}
        >
          <div className="flex cursor-pointer items-center gap-2 rounded-md p-1 hover:bg-gray-100">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-blue-100 text-sm font-medium">
              {userName?.substring(0, 2).toUpperCase()}
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-medium text-gray-900">
                {userName}
              </span>
              <span className="text-xs text-gray-500">{userRole}</span>
            </div>
          </div>
          <FontAwesomeIcon
            icon={faChevronDown}
            className="cursor-pointer items-end p-1 text-xs"
          />
        </div>
        {/* Dropdown menu */}
        {userMenuOpen && (
          <div
            className="animate-fade-in absolute bottom-14 left-0 z-50 flex w-72 flex-col rounded-xl border bg-white p-0 shadow-xl"
            style={{ minWidth: '260px' }}
          >
            {/* Account section */}
            <div className="border-b px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-base font-medium">
                  {userName?.substring(0, 2).toUpperCase()}
                </div>
                <div className="flex flex-col">
                  <span className="text-base font-medium text-gray-900">
                    {userName}
                  </span>
                  <span className="text-xs text-gray-500">{userEmail}</span>
                </div>
              </div>
              {/* <button className="mt-2 text-xs text-blue-700 hover:underline flex items-center gap-1">
                Manage account
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </button> */}
            </div>
            {/* Account section */}
            {/* <div className="border-b px-4 py-2">
              <div className="text-xs text-gray-500 mb-1">Z Recruitment</div>
              <button className="w-full rounded py-1 text-left text-sm hover:bg-gray-100">
                Profile
              </button>
              <button className=" w-full text-left py-1 text-sm hover:bg-gray-100 rounded">
                Personal settings
              </button>
              <button className=" w-full text-left py-1 text-sm hover:bg-gray-100 rounded flex items-center gap-2">
                Notifications{' '}
              </button>
              <button className=" w-full text-left py-1 text-sm hover:bg-gray-100 rounded flex items-center">
                Theme{' '}
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className="ml-auto text-xs"
                />
              </button>
            </div> */}
            {/* Log out */}
            <button
              onClick={signOut}
              className="w-full rounded-b-xl px-4 py-3 text-left text-sm text-red-600 hover:bg-gray-100"
            >
              Log out
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
